import React, { useEffect, useState } from 'react';
import { DetailsTemplate, EDetailsPageTypes } from 'components/templates/DetailsTemplate';
import { ERouteLinks } from 'models/route';
import { Box, Grid, Stack, Typography } from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import { getUser } from 'requests/user';
import { IUser } from 'models/user';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { IParsedDictionary, parseDictionary } from 'helpers/dictionary';
import { EUserStatusActivated } from 'pages/UsersPage/helpers';
import { Chips, EChipsVariants } from 'components/atoms/Chips';
import { Alert } from 'components/atoms/Alert';
import { useAlert } from 'models/alertContext';
import { ActionTypes } from 'state/actions/alert';
import { useTranslations } from 'hooks/useTranslations';
import { getRoles } from 'requests/role';
import { EPermission } from 'models/permissions';
import { usePermission } from 'hooks/usePermission';
import { useAccount } from 'pages/AccountPage/hooks';
import { formatDate } from 'utils/date';
import { useGlobalProperty } from 'models/globalPropertyContext';
import { useUsers } from 'pages/UsersPage/hooks';
import { useAuth } from 'models/authContext';
import { useWeaponLicenceModule } from 'hooks/useWeaponLicenceModule';
import { EOrganisationCategory } from 'constants/OrganisationCategory';
import { useDictionary } from 'models/dictionaryContext';

enum EViewUserPageTranslationsKeys {
  editLink = 'userDetails.edit.link',
  deleteLink = 'userDetails.delete.link',
}

export const ViewUserPage = () => {
  const navigate = useNavigate();
  const { id = '' } = useParams<{ id: string }>();
  const [userDetails, setUserDetails] = useState<IUser | null>();
  const [roleNames, setRoleNames] = useState<string[]>([]);
  const [prefixNames, setPrefixNames] = useState<string[]>([]);
  const [orgCategoryNames, setOrgCategoryNames] = useState<string[]>([]);
  const [licenceAuthority, setLicenceAuthority] = useState<string[]>([]);
  const [userActivated, setUserActivated] = useState<boolean>();
  const [isAssignedLELoading, setIsAssignedLELoading] = useState<boolean>(false);
  const [isUserLoading, setIsUserLoading] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [showLicenceAuthority, setShowLicenceAuthority] = useState<boolean>(false);
  const { phoneCode, legalEntityPrefix, organisationCategory, weaponLicencingAuthority } =
    useDictionary();
  const { loadDictionaries } = useUsers();
  const { getAssignedLegalsToUserData } = useAccount();
  const { clearAlert, legalEntityAlert, userAlert } = useAlert();
  const { t } = useTranslations();
  const { hasPermissions } = usePermission();
  const { hasWeaponLicenceModule } = useWeaponLicenceModule();
  const { longDateFormat, getProperties } = useGlobalProperty();
  const { user } = useAuth();

  useEffect(() => {
    getProperties();
    loadDictionaries();
  }, []);

  useEffect(() => {
    (async function getUserDataInit() {
      setIsUserLoading(true);
      if (id) {
        const response = await getUser(id);
        if (response) {
          setUserDetails(response);
          setUserActivated(response.active);

          if (response.organisationCategoryIds) {
            setShowLicenceAuthority(
              response.organisationCategoryIds.includes(EOrganisationCategory.civilian),
            );
          }

          if (response.roleIds && response.roleIds.length) {
            const responseRoles = await getRoles(1, 100, {}, null);
            if (responseRoles) {
              const roles = responseRoles.content
                .map((role) => response.roleIds.includes(role.uniqueId) && role.name)
                .filter((row) => !!row);
              setRoleNames(roles as string[]);
            }
          }
        }
      }
      setIsUserLoading(false);
    })();
  }, [id]);

  useEffect(() => {
    if (!userDetails) return;

    if (userDetails.prefixGroupIds?.length && legalEntityPrefix) {
      const names = legalEntityPrefix
        .filter((prefix) => userDetails.prefixGroupIds.includes(prefix.uniqueId.toString()))
        .map((prefix) => prefix.name);
      setPrefixNames(names);
    }

    if (userDetails.organisationCategoryIds?.length && organisationCategory) {
      const names = organisationCategory
        .filter((category) =>
          userDetails.organisationCategoryIds.includes(category.uniqueId.toString()),
        )
        .map((category) => category.name);
      setOrgCategoryNames(names);
    }

    if (userDetails.licenceAuthorityIds?.length && weaponLicencingAuthority) {
      const names = weaponLicencingAuthority
        .filter((item) => userDetails.licenceAuthorityIds.includes(item.uniqueId.toString()))
        .map((item) => item.name);
      setLicenceAuthority(names);
    }
  }, [userDetails, legalEntityPrefix, organisationCategory, weaponLicencingAuthority]);

  useEffect(() => {
    (async function getAssignedLEsInit() {
      if (id && hasPermissions(EPermission.VIEW_LE)) {
        setIsAssignedLELoading(true);
        await getAssignedLegalsToUserData(id);
        setIsAssignedLELoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    if (!isUserLoading && !isAssignedLELoading) {
      setIsLoading(false);
    }
  }, [isAssignedLELoading, isUserLoading]);

  useEffect(
    () => () => {
      if (!isLoading) {
        clearAlert(ActionTypes.CLEAR_LEGAL_ENTITY_ALERT);
        clearAlert(ActionTypes.CLEAR_USER_ALERT);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isLoading],
  );

  const parsedPhoneCodeDictionary: IParsedDictionary = parseDictionary(phoneCode, 'name');

  const detailsTemplateProps = () => {
    // todo
    if (hasPermissions([EPermission.MANAGE_USERS])) {
      return {
        redirectToEditView: () => navigate(`${ERouteLinks.EditUser.replace(':id', id)}`),
        changeActivationUserStatus: () => {
          if (userDetails) {
            navigate(
              `${ERouteLinks.EditUserStatus.replace(':id', id).replace(
                ':status',
                userActivated
                  ? EUserStatusActivated.deactivate
                  : EUserStatusActivated.activate,
              )}`,
            );
          }
        },
        handleDelete: !userDetails?.removed
          ? () => {
            if (userDetails) {
              navigate(
                `${ERouteLinks.DeleteUser.replace(':id', id).replace(
                  ':status',
                  userActivated
                    ? EUserStatusActivated.deactivate
                    : EUserStatusActivated.activate,
                )}`,
              );
            }
          }
          : undefined,
        unbanUserStatus: () => {
          if (userDetails) {
            navigate(
              `${ERouteLinks.EditUserStatus.replace(':id', id).replace(
                ':status',
                userActivated
                  ? EUserStatusActivated.deactivate
                  : EUserStatusActivated.activate,
              )}`,
            );
          }
        },
        redirectToGeneratePasswordView: () =>
          navigate(`${ERouteLinks.GeneratePasswordUser.replace(':id', id)}`),
        redirectToView: () => null,
        resetOfflineAuthenticator: () => {
          if (userDetails) {
            navigate(`${ERouteLinks.ResetUserOfflineAuth.replace(':id', id)}`);
          }
        },
      };
    }
    return {
      redirectToEditView: () => navigate(`${ERouteLinks.EditUser.replace(':id', id)}`),
      redirectToView: () => null,
    };
  };

  const statusChip = () => {
    if (userDetails) {
      if (userDetails.removed) {
        return <Chips text={t('users.removed.label')} variant={EChipsVariants.error} />;
      }
      if (userDetails.blocked) {
        return <Chips text={t('users.blocked.label')} variant={EChipsVariants.warning} />;
      }
      return (
        <Chips
          text={userDetails.active ? t('users.active.label') : t('users.inactive.label')}
          variant={userDetails.active ? EChipsVariants.success : EChipsVariants.error}
        />
      );
    }
    return '-';
  };

  return (
    <>
      <Breadcrumbs
        items={[
          { label: t('user.breadcrumbs.administration'), route: ERouteLinks.Administration },
          { label: t('user.breadcrumbs.users'), route: ERouteLinks.Users },
          t('user.breadcrumbs.userDetails'),
        ]}
      />
      {userDetails && user && user.userId && (
        <DetailsTemplate
          pageType={EDetailsPageTypes.userView}
          title={userDetails.email}
          {...detailsTemplateProps()}
          userActivated={userActivated}
          userBlocked={userDetails.blocked}
          disableActions={userDetails.removed}
          actionButtonFullRow
          deletePermissions={[EPermission.MANAGE_USERS]}
          translationsKeys={EViewUserPageTranslationsKeys}
          loggedUserIdEqualsViewUserId={userDetails.uniqueId === user.userId}
        >
          <Grid size={{ xs: 6, lg: 3 }}>
            <Stack>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('userDetails.displayUniqueId.label')}
              </Typography>
              <div>{userDetails.displayUniqueId}</div>
            </Stack>
          </Grid>
          <Grid size={{ xs: 6, lg: 3 }}>
            <Stack>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('userDetails.firstName.label')}
              </Typography>
              <Typography variant="body2">{userDetails.firstName}</Typography>
            </Stack>
          </Grid>
          <Grid size={{ xs: 6, lg: 3 }}>
            <Stack>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('userDetails.lastName.label')}
              </Typography>
              <Typography variant="body2">{userDetails.lastName}</Typography>
            </Stack>
          </Grid>
          <Grid size={{ xs: 6, lg: 3 }}>
            <Stack>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('userDetails.phoneNumber.label')}
              </Typography>
              <Typography variant="body2">{`${userDetails.phoneNumber &&
                parsedPhoneCodeDictionary?.[userDetails.phoneNumber.codeDictionaryUniqueId]
                ? `${parsedPhoneCodeDictionary?.[userDetails.phoneNumber.codeDictionaryUniqueId]
                }${userDetails.phoneNumber.value}`
                : '-'
                }`}</Typography>
            </Stack>
          </Grid>
          <Grid size={{ xs: 6, lg: 3 }}>
            <Stack>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('userDetails.lastLogged.label')}
              </Typography>
              <Typography variant="body2">
                {!userDetails.removed
                  ? formatDate(userDetails.lastLoginDate, longDateFormat)
                  : '-'}
              </Typography>
            </Stack>
          </Grid>
          <Grid size={{ xs: 6, lg: 3 }}>
            <Stack>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('userDetails.role.label')}
              </Typography>
              <div>
                {roleNames.length
                  ? roleNames.map((roleItem) => (
                    <Chips key={roleItem} text={roleItem} variant={EChipsVariants.primary} />
                  ))
                  : '-'}
              </div>
            </Stack>
          </Grid>
          <Grid size={{ xs: 6, lg: 3 }}>
            <Stack>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('userDetails.status.label')}
              </Typography>
              <div>{statusChip()}</div>
            </Stack>
          </Grid>
          <Grid size={{ xs: 6, lg: 3 }}>
            <Stack>
              <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                {t('userDetails.prefix.label')}
              </Typography>
              <div>
                {[...new Set([...prefixNames, ...orgCategoryNames])].map((item) => (
                  <Chips key={item} text={item} variant={EChipsVariants.primary} />
                ))}
              </div>
            </Stack>
          </Grid>
          {showLicenceAuthority && hasWeaponLicenceModule() && (
            <Grid size={{ xs: 6, lg: 3 }}>
              <Stack>
                <Typography variant="subtitle2" sx={{ marginBottom: '4px' }}>
                  {t('userDetails.licencingAuthority.label')}
                </Typography>
                <div>
                  {licenceAuthority.length > 1 ? (
                    <Chips text={t('general.chip.all')} variant={EChipsVariants.primary} />
                  ) : (
                    licenceAuthority.map((item) => (
                      <Chips key={item} text={item} variant={EChipsVariants.primary} />
                    ))
                  )}
                </div>
              </Stack>
            </Grid>
          )}
        </DetailsTemplate >
      )}
      {
        legalEntityAlert && (
          <Box sx={{ mt: 2 }}>
            <Alert text={legalEntityAlert.text} variant={legalEntityAlert.variant} />
          </Box>
        )
      }
      {
        userAlert && (
          <Box sx={{ mt: 2 }}>
            <Alert text={userAlert.text} variant={userAlert.variant} />
          </Box>
        )
      }
    </>
  );
};
